import React from 'react';
import { getTextPresentation, resolveTextLanguage } from './ResultFunctions';


const URDU_CHARACTERS = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/;
const HIGHLIGHTED_TEXT = /<b\s+class=["']highlight["']>(.*?)<\/b>/gi;
const QA_LABELS = {
    en: { question: 'Question:', answer: 'Answer:' },
    ur: { question: 'سوال:', answer: 'جواب:' },
}


export const containsUrduText = html => (
    URDU_CHARACTERS.test(String(html || '').replace(/<[^>]*>/g, ''))
)


export const splitHuzoorText = html => (
    String(html || '')
        .split(/(?:<br\s*\/?\s*>|\r?\n)+/i)
        .filter(line => line.trim())
)


const escapeHtml = value => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')


const escapeRegExp = value => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')


export const getHighlightedTerms = html => {
    const terms = []
    let match

    while ((match = HIGHLIGHTED_TEXT.exec(String(html || ''))) !== null) {
        const term = match[1].replace(/<[^>]*>/g, '')
        if (term && !terms.includes(term)) {
            terms.push(term)
        }
    }
    HIGHLIGHTED_TEXT.lastIndex = 0

    return terms
}


const addHighlights = (value, terms) => {
    const escapedValue = escapeHtml(value)
    const escapedTerms = terms
        .map(escapeHtml)
        .filter(Boolean)
        .sort((first, second) => second.length - first.length)

    if (!escapedTerms.length) {
        return escapedValue
    }

    const pattern = new RegExp(escapedTerms.map(escapeRegExp).join('|'), 'gi')
    return escapedValue.replace(pattern, match => `<b class="highlight">${match}</b>`)
}


const getQaFieldLanguage = (field, source, value) => {
    const fieldLanguage = String(source[`${field}_language`] || source.qa_language || '')
        .trim()
        .toLowerCase()
    if (fieldLanguage) {
        return resolveTextLanguage(fieldLanguage)
    }
    return containsUrduText(value) ? 'ur' : 'en'
}


const getQaFieldLabel = (field, source, value) => {
    const language = getQaFieldLanguage(field, source, value)

    return QA_LABELS[language === 'ur' ? 'ur' : 'en'][field]
}


export const getHuzoorDisplayLines = hit => {
    const source = hit._source || {}
    const highlights = hit.highlight || {}
    const highlightedText = Object.keys(highlights)
        .reduce((values, field) => values.concat(highlights[field] || []), [])
        .join('\n')

    if (source.document_type !== 'qa_pair') {
        return splitHuzoorText(highlightedText || source.text || '')
            .map(html => ({ html, language: resolveTextLanguage(source.lang, hit._index) }))
    }

    const terms = getHighlightedTerms(highlightedText)
    const visibleFields = [
        { field: 'context', value: source.context },
        { field: 'question', value: source.question },
        { field: 'answer', value: source.answer },
    ]
        .map(item => ({ ...item, value: String(item.value || '').trim() }))
        .filter((item, index, items) => (
            item.value && items.findIndex(candidate => candidate.value === item.value) === index
        ))

    return visibleFields
        .map(({ field, value }) => {
            const highlightedValue = addHighlights(value, terms)
            if (field === 'context') {
                return {
                    html: highlightedValue,
                    language: getQaFieldLanguage(field, source, value),
                }
            }
            return {
                html: `${getQaFieldLabel(field, source, value)} ${highlightedValue}`,
                language: getQaFieldLanguage(field, source, value),
            }
        })
}


export const getHuzoorDisplayHtml = hit => (
    getHuzoorDisplayLines(hit).map(line => line.html).join('\n\n')
)


const HuzoorQaText = ({ hit, html, onClick }) => {
    const lines = hit
        ? getHuzoorDisplayLines(hit)
        : splitHuzoorText(html).map(line => ({
            html: line,
            language: containsUrduText(line) ? 'ur' : 'en',
        }))

    return <div className="huzoor-qa-text" onClick={onClick}>
        {lines.map((line, index) => {
            const textPresentation = getTextPresentation(line.language, hit && hit._index)
            const lineType = textPresentation.language === 'ur'
                ? 'urdu'
                : textPresentation.language === 'ar' ? 'arabic' : 'ltr'

            return <p
                key={`${index}-${line.html}`}
                className={`huzoor-qa-line huzoor-qa-line-${lineType} ${textPresentation.className}`}
                dir={textPresentation.dir}
                lang={textPresentation.lang}
                dangerouslySetInnerHTML={{__html: line.html}}
            />
        })}
    </div>
}


export default HuzoorQaText;
